"use client"

import { TextInput, PasswordInput, Checkbox, Stack, Text, Button, Group, Title, rem, useMantineTheme, Card, Grid } from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { z } from "zod"
import { useTranslations } from "next-intl"
import { IconUser, IconBuilding, IconAt, IconChevronRight } from "@tabler/icons-react"
import { PasswordStrength } from "./PasswordStrength"
import { PhoneInput, validatePhoneNumber } from "@/components/generic/PhoneInput"
import { useRouter } from "next/navigation"
import { useTransition, useState } from "react"
import { actionVerifySignup } from "@/app/api/signUp/verify/actions"
import { showNotification } from "@/lib/notifications"
import { mapApiErrorsToFormErrors } from "./helpers"
import { SignupSuccessOverlay } from "./SignupSuccessOverlay"
import type { TApiSignupCreateParams } from "@/api/launcherApp/signup/schema"
import { useDisclosure } from "@mantine/hooks"

const signUpSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  company: z.string().min(2, "Company name must be at least 2 characters"),
  phone: z.string().refine((val) => {
    const parsedNumber = validatePhoneNumber(val)
    return parsedNumber !== ""
  }, "Invalid phone number"),
  email: z.string().email("Invalid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  termsAccepted: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions",
  }),
  smsAccepted: z.boolean(),
  promotionalEmails: z.boolean(),
})

type SignUpFormValues = z.infer<typeof signUpSchema> & {
  username: string
}

export function SignUpForm({ plan }: { plan: "team" | "solo" }) {
  const t = useTranslations("auth.signUp")
  const tErrors = useTranslations("auth.errors")
  const theme = useMantineTheme()
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [showSuccessOverlay, { open: openSuccessOverlay, close: closeSuccessOverlay }] = useDisclosure(false)
  const [createParams, setCreateParams] = useState<TApiSignupCreateParams | undefined>(undefined)

  const form = useForm<SignUpFormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      company: "",
      phone: "",
      email: "",
      password: "",
      termsAccepted: false,
      smsAccepted: false,
      promotionalEmails: false,
      username: "",
    },
    /*
    initialValues: {
      firstName: "John",
      lastName: "Doe",
      company: "Acme Inc.",
      phone: "+15712529423",
      email: "john.doeeeeeeeeeeeeeeee@example.com",
      password: "Password123!",
      termsAccepted: true,
      smsAccepted: true,
      promotionalEmails: true,
      username: "",
    },
    */
    validate: zodResolver(signUpSchema),
  })

  const handleSubmit = async (values: SignUpFormValues) => {
    if (values.username) {
      router.push("https://www.google.com")
      return
    }

    startTransition(async () => {
      const result = await actionVerifySignup({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phone,
        companyName: values.company,
        smsOptIn: values.smsAccepted,
        emailMarketingOptIn: values.promotionalEmails,
        trialType: plan,
        passwd: values.password,
      })

      if (result.success === false || !result.data?.success) {
        if (result.data?.errors) {
          const formErrors = mapApiErrorsToFormErrors(result.data.errors, tErrors)
          Object.entries(formErrors).forEach(([field, message]) => {
            form.setFieldError(field, message)
          })

          window.scrollTo({ top: 0, behavior: "smooth" })
        } else {
          showNotification({
            type: "error",
            title: t("genericError.title"),
            message: t("genericError.message"),
          })
        }
      } else {
        setCreateParams(result.data.cookies ?? [])

        openSuccessOverlay()
      }
    })
  }

  const handleSuccess = () => {
    closeSuccessOverlay()
    setCreateParams(undefined)
    // router.push("/dashboard")
  }

  const handleError = () => {
    closeSuccessOverlay()
    setCreateParams(undefined)
    showNotification({
      type: "error",
      autoClose: 10000,
      title: t("signupError.title"),
      message: t("signupError.message"),
    })
  }

  return (
    <>
      <Card withBorder shadow="sm" radius="md" maw={rem(800)}>
        <Stack gap="xl" p="xl">
          <Stack gap={rem(6)}>
            <Title order={3}>Create your {plan === "team" ? "team" : "personal"} account</Title>
            <Text c="dimmed" size="sm">
              Fill in your information to get started
            </Text>
          </Stack>

          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput disabled={isPending} required label={t("firstName")} placeholder="John" leftSection={<IconUser size={16} />} {...form.getInputProps("firstName")} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput disabled={isPending} required label={t("lastName")} placeholder="Doe" leftSection={<IconUser size={16} />} {...form.getInputProps("lastName")} />
            </Grid.Col>
          </Grid>

          <TextInput disabled={isPending} required label={t("company")} placeholder="Acme Inc." leftSection={<IconBuilding size={16} />} {...form.getInputProps("company")} />

          <PhoneInput size="sm" required label={t("phone")} placeholder={t("phonePlaceholder")} value={form.values.phone} onChangePhone={(value) => form.setFieldValue("phone", value)} error={form.errors.phone} allowedCountries={false} />

          <TextInput disabled={isPending} required label={t("email")} description="Use your direct work email address" placeholder="john.doe@example.com" leftSection={<IconAt size={16} />} {...form.getInputProps("email")} />

          <Stack gap="xs">
            <PasswordInput disabled={isPending} required label={t("password")} placeholder={t("passwordPlaceholder")} {...form.getInputProps("password")} />
            <PasswordStrength password={form.values.password} />
          </Stack>

          <Stack gap="md">
            <Checkbox
              required
              disabled={isPending}
              label={
                <Text size="sm" c="dimmed">
                  I have read and agree to the{" "}
                  <Text component="a" href="https://www.ring.io/terms-and-conditions/" target="_blank" rel="noopener noreferrer" span c={theme.primaryColor} td="underline">
                    Terms and Conditions
                  </Text>
                </Text>
              }
              {...form.getInputProps("termsAccepted", { type: "checkbox" })}
            />

            <Checkbox
              disabled={isPending}
              label={
                <Text size="sm" c="dimmed">
                  Allow Ring.io to SMS me with info and reminders about Ring.io products at the phone number provided. Your carrier messaging rates may apply. Reply STOP to stop.
                </Text>
              }
              {...form.getInputProps("smsAccepted", { type: "checkbox" })}
            />

            <Checkbox
              disabled={isPending}
              label={
                <Text size="sm" c="dimmed">
                  Send me occasional emails about Ring.io
                </Text>
              }
              {...form.getInputProps("promotionalEmails", { type: "checkbox" })}
            />
          </Stack>

          <TextInput disabled={isPending} style={{ display: "none" }} aria-hidden="true" tabIndex={-1} {...form.getInputProps("username")} />

          <Button
            fullWidth
            size="lg"
            rightSection={
              <IconChevronRight
                size={21}
                stroke={2.5}
                style={{
                  marginTop: 2,
                }}
              />
            }
            disabled={!form.values.termsAccepted}
            onClick={() => form.onSubmit(handleSubmit)()}
            loading={isPending}
          >
            Create Account
          </Button>
        </Stack>
      </Card>
      <SignupSuccessOverlay onSuccess={handleSuccess} onError={handleError} userData={{ email: form.values.email, password: form.values.password, plan }} createParams={createParams} opened={showSuccessOverlay} onClose={closeSuccessOverlay} />
    </>
  )
}
